import { SourceStamp } from '../../types/marketing';
import { CONSOLE_HINTS, dataAsOfDay, formatLongDay, SOURCE_ORDER, sourceLabel } from './marketingMath';

const STATUS_TEXT: Record<string, string> = { OK: 'OK', FAILED: 'Failed', NOT_CONNECTED: 'Not connected', NEVER: 'No run yet' };

function chipClass(status: string): string {
  if (status === 'OK') return 'mkt-chip mkt-chip--ok';
  if (status === 'FAILED') return 'mkt-chip mkt-chip--failed';
  return 'mkt-chip mkt-chip--off';
}

/** A small shape next to the text, so the status never depends on colour alone. */
function StatusIcon({ status }: { status: string }) {
  if (status === 'OK') return <svg className="mkt-chip__icon" viewBox="0 0 12 12" aria-hidden="true"><path d="M2.5 6.5l2.2 2.2 4.8-5" fill="none" strokeWidth="1.8" /></svg>;
  if (status === 'FAILED') return <svg className="mkt-chip__icon" viewBox="0 0 12 12" aria-hidden="true"><path d="M3 3l6 6M9 3l-6 6" fill="none" strokeWidth="1.8" /></svg>;
  return <svg className="mkt-chip__icon" viewBox="0 0 12 12" aria-hidden="true"><circle cx="6" cy="6" r="3.5" fill="none" strokeWidth="1.6" /></svg>;
}

/**
 * Spec 084 — "Data as of <day> (Dubai)" plus one chip per source: OK (last OK day), FAILED (red, error code),
 * NOT_CONNECTED (grey, where to switch it on) or NEVER (grey, no run yet).
 */
export default function DataStamp({ dataAsOf }: { dataAsOf: Record<string, SourceStamp> }) {
  const keys = [...SOURCE_ORDER.filter((k) => k in dataAsOf), ...Object.keys(dataAsOf).filter((k) => !SOURCE_ORDER.includes(k))];
  const asOf = dataAsOfDay(dataAsOf);
  return (
    <section className="mkt-stamp" aria-label="Data sources" data-testid="data-stamp">
      <p className="mkt-stamp__asof" data-testid="data-stamp-asof">
        {asOf ? `Data as of ${formatLongDay(asOf)} (Dubai)` : 'No source has data for this range yet'}
      </p>
      <ul className="mkt-stamp__chips">
        {keys.map((k) => {
          const s = dataAsOf[k];
          const status = String(s.status);
          const detail = status === 'OK' ? `last day ${formatLongDay(s.day)}`
            : status === 'FAILED' ? `${s.errorCode ?? 'error'}${s.day ? `, last OK ${formatLongDay(s.day)}` : ''}`
              : status === 'NOT_CONNECTED' ? (CONSOLE_HINTS[k] ?? 'Not set up yet') : 'The nightly pull has not run for this source';
          return (
            <li key={k} className={chipClass(status)} data-testid={`source-chip-${k}`} data-status={status} title={detail}>
              <StatusIcon status={status} />
              <span className="mkt-chip__name">{sourceLabel(k)}</span>
              <span className="mkt-chip__status">{STATUS_TEXT[status] ?? status}</span>
              {status === 'FAILED' && s.errorCode && <code className="mkt-chip__code">{s.errorCode}</code>}
            </li>
          );
        })}
      </ul>
      {keys.some((k) => String(dataAsOf[k].status) === 'NOT_CONNECTED') && (
        <ul className="mkt-stamp__hints" data-testid="data-stamp-hints">
          {keys.filter((k) => String(dataAsOf[k].status) === 'NOT_CONNECTED').map((k) => (
            <li key={k}>{CONSOLE_HINTS[k] ?? `${sourceLabel(k)}: not set up yet.`}</li>
          ))}
        </ul>
      )}
    </section>
  );
}
